import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, CheckCircle, XCircle, Loader2, FileSpreadsheet, AlertCircle, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  useImportHistory,
  useUploadImport,
  useUploadWorkbook,
  type ImportType,
  type ImportStatus,
  type ImportError,
  type WorkbookImportResult,
  type WorkbookImportEntry,
} from '../hooks/useImports';
import { parseGPWorkbook, type GPWorkbookResult } from '../lib/excelParser';

const statusColors: Record<ImportStatus, string> = {
  validating: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  complete: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

function StatusBadge({ status }: { status: ImportStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-700'}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function ImportErrorList({ errors }: { errors: ImportError[] }) {
  const displayErrors = errors.slice(0, 10);
  const remainingCount = errors.length - displayErrors.length;

  return (
    <div className="mt-3 max-h-48 overflow-auto">
      <p className="text-sm font-medium text-danger-foreground mb-2">
        Validation errors ({errors.length} total):
      </p>
      <div className="space-y-1">
        {displayErrors.map((err, i) => (
          <p key={i} className="text-sm text-danger-foreground font-mono">
            Row {err.row}: <span className="font-medium">{err.field}</span> - {err.message}
          </p>
        ))}
      </div>
      {remainingCount > 0 && (
        <p className="text-sm text-danger-foreground mt-2 italic">
          ...and {remainingCount} more errors
        </p>
      )}
    </div>
  );
}

/** Summary card for a detected sheet */
function SheetSummaryCard({
  label,
  rowCount,
  warningCount,
  icon,
}: {
  label: string;
  rowCount: number;
  warningCount: number;
  icon: string;
}) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-background">
      <span className="text-2xl">{icon}</span>
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-foreground-muted">
          {rowCount.toLocaleString()} rows
          {warningCount > 0 && (
            <span className="text-warning ml-2">({warningCount} warnings)</span>
          )}
        </p>
      </div>
    </div>
  );
}

export function ImportsPage() {
  // Workbook state (Excel drop)
  const [workbookPreview, setWorkbookPreview] = useState<GPWorkbookResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  // CSV fallback state
  const [csvImportType, setCsvImportType] = useState<ImportType>('sales-orders');
  const [showCsvMode, setShowCsvMode] = useState(false);

  const { data: imports, isLoading, error } = useImportHistory();
  const csvUploadMutation = useUploadImport();
  const workbookMutation = useUploadWorkbook();

  const isBusy = csvUploadMutation.isPending || workbookMutation.isPending;

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setWorkbookPreview(null);
      setParseError(null);

      if (file.name.endsWith('.csv')) {
        if (showCsvMode) {
          // CSV mode with type selector
          csvUploadMutation.mutate({ type: csvImportType, file });
        } else {
          setParseError('Drop a GP Excel workbook (.xlsx) here. For CSV imports, click "Use CSV instead" below.');
        }
      } else if (file.name.endsWith('.xlsx')) {
        try {
          const result = await parseGPWorkbook(file);
          const hasData =
            (result.inventory && result.inventory.rows.length > 0) ||
            (result.purchaseOrders && result.purchaseOrders.rows.length > 0) ||
            (result.salesOrders && result.salesOrders.rows.length > 0);

          if (!hasData) {
            setParseError(
              `No supported sheets found. Detected sheets: ${result.sheetsFound.join(', ')}. ` +
              'Expected: SalesLine, POLine, or InventoryTX&Wheeling.'
            );
          } else {
            setWorkbookPreview(result);
          }
        } catch (err) {
          setParseError(err instanceof Error ? err.message : 'Failed to parse Excel file');
        }
      }
    },
    [showCsvMode, csvImportType, csvUploadMutation]
  );

  const handleWorkbookImport = () => {
    if (!workbookPreview) return;
    workbookMutation.mutate(
      {
        inventory: workbookPreview.inventory ? { rows: workbookPreview.inventory.rows } : undefined,
        purchaseOrders: workbookPreview.purchaseOrders ? { rows: workbookPreview.purchaseOrders.rows } : undefined,
        salesOrders: workbookPreview.salesOrders ? { rows: workbookPreview.salesOrders.rows } : undefined,
      },
      {
        onSuccess: () => {
          setWorkbookPreview(null);
        },
      }
    );
  };

  const handleCancelPreview = () => {
    setWorkbookPreview(null);
    setParseError(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: showCsvMode
      ? {
          'text/csv': ['.csv'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        }
      : {
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          'text/csv': ['.csv'],
        },
    maxFiles: 1,
    disabled: isBusy,
  });

  // Collect all warnings from parsed workbook
  const allWarnings = workbookPreview
    ? [
        ...(workbookPreview.inventory?.warnings ?? []).map((w) => `Inventory: ${w}`),
        ...(workbookPreview.purchaseOrders?.warnings ?? []).map((w) => `POs: ${w}`),
        ...(workbookPreview.salesOrders?.warnings ?? []).map((w) => `Sales: ${w}`),
      ]
    : [];

  // Count detected sheets for preview
  const detectedSheets: Array<{ label: string; rows: number; warnings: number; icon: string }> = [];
  if (workbookPreview?.salesOrders) {
    detectedSheets.push({
      label: 'Sales Orders',
      rows: workbookPreview.salesOrders.rows.length,
      warnings: workbookPreview.salesOrders.warnings.length,
      icon: '\u{1F4CB}',
    });
  }
  if (workbookPreview?.purchaseOrders) {
    detectedSheets.push({
      label: 'Purchase Orders',
      rows: workbookPreview.purchaseOrders.rows.length,
      warnings: workbookPreview.purchaseOrders.warnings.length,
      icon: '\u{1F4E6}',
    });
  }
  if (workbookPreview?.inventory) {
    detectedSheets.push({
      label: 'Inventory',
      rows: workbookPreview.inventory.rows.length,
      warnings: workbookPreview.inventory.warnings.length,
      icon: '\u{1F4E6}',
    });
  }

  const totalRows = detectedSheets.reduce((sum, s) => sum + s.rows, 0);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">GP Imports</h1>
        <p className="text-sm text-foreground-muted mt-1">
          Drop a GP Excel workbook to import inventory, purchase orders, and sales orders at once
        </p>
      </div>

      {/* Main import area */}
      <div className="bg-background rounded-lg border border-border p-6">
        {/* CSV mode toggle */}
        {showCsvMode && (
          <div className="mb-4 flex items-center gap-4">
            <label htmlFor="csvType" className="text-sm font-medium text-foreground">
              CSV Import Type
            </label>
            <select
              id="csvType"
              value={csvImportType}
              onChange={(e) => setCsvImportType(e.target.value as ImportType)}
              disabled={isBusy}
              className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="sales-orders">Sales Orders</option>
              <option value="inventory">Inventory</option>
              <option value="purchase-orders">Purchase Orders</option>
            </select>
            <button
              onClick={() => setShowCsvMode(false)}
              className="text-sm text-primary hover:text-primary/80"
            >
              Back to Excel mode
            </button>
          </div>
        )}

        {/* Dropzone */}
        {!workbookPreview && (
          <>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isBusy
                  ? 'border-border bg-background-muted cursor-not-allowed opacity-50'
                  : isDragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50 hover:bg-background-subtle'
              }`}
            >
              <input {...getInputProps()} />
              <Upload
                className={`mx-auto mb-4 ${isDragActive ? 'text-primary' : 'text-foreground-muted'}`}
                size={48}
              />
              <p className="text-foreground">
                {isDragActive
                  ? 'Drop the file here'
                  : showCsvMode
                  ? 'Drag & drop a CSV file, or click to select'
                  : 'Drag & drop a GP Excel workbook (.xlsx), or click to select'}
              </p>
              <p className="text-sm text-foreground-muted mt-2">
                {showCsvMode
                  ? 'Maximum 10MB, 10,000 rows'
                  : 'Auto-detects SalesLine, POLine, and Inventory sheets'}
              </p>
            </div>

            {!showCsvMode && (
              <button
                onClick={() => setShowCsvMode(true)}
                className="mt-3 text-sm text-foreground-muted hover:text-foreground"
              >
                Use CSV instead
              </button>
            )}
          </>
        )}

        {/* Workbook Preview */}
        {workbookPreview && (
          <div className="p-4 rounded-lg border border-primary bg-primary/5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-foreground">Workbook Preview</h3>
                <p className="text-sm text-foreground-muted">
                  {detectedSheets.length} sheet{detectedSheets.length !== 1 ? 's' : ''} detected — {totalRows.toLocaleString()} total rows
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancelPreview}
                  disabled={workbookMutation.isPending}
                  className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground hover:bg-background-subtle transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleWorkbookImport}
                  disabled={workbookMutation.isPending || totalRows === 0}
                  className="px-4 py-1.5 text-sm font-medium rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
                >
                  {workbookMutation.isPending ? 'Importing...' : 'Import All'}
                </button>
              </div>
            </div>

            {/* Sheet summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              {detectedSheets.map((sheet) => (
                <SheetSummaryCard
                  key={sheet.label}
                  label={sheet.label}
                  rowCount={sheet.rows}
                  warningCount={sheet.warnings}
                  icon={sheet.icon}
                />
              ))}
            </div>

            {/* Other sheets detected */}
            {workbookPreview.sheetsFound.length > detectedSheets.length && (
              <p className="text-xs text-foreground-muted mb-3">
                Other sheets found (not imported):{' '}
                {workbookPreview.sheetsFound
                  .filter((s) => s !== 'SalesLine' && s !== 'POLine' && s !== 'InventoryTX&Wheeling' && s !== 'LocationBIN')
                  .join(', ')}
              </p>
            )}

            {/* Warnings */}
            {allWarnings.length > 0 && (
              <div className="p-3 rounded border border-warning bg-warning-subtle">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  <span className="text-sm font-medium text-foreground">{allWarnings.length} warning(s)</span>
                </div>
                <ul className="text-xs text-foreground-muted space-y-0.5 max-h-24 overflow-auto">
                  {allWarnings.slice(0, 15).map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                  {allWarnings.length > 15 && (
                    <li className="italic">...and {allWarnings.length - 15} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Parse error */}
        {parseError && (
          <div className="mt-4 p-4 rounded-lg border border-danger bg-danger-subtle">
            <div className="flex items-center gap-3">
              <XCircle className="h-5 w-5 text-danger" />
              <span className="font-medium text-danger-foreground">Failed to parse file</span>
            </div>
            <p className="text-sm text-danger-foreground mt-2">{parseError}</p>
            <button
              onClick={() => setParseError(null)}
              className="mt-2 text-sm text-danger-foreground underline hover:no-underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* CSV Upload Status */}
        {csvUploadMutation.isPending && (
          <div className="mt-4 p-4 rounded-lg border border-primary bg-primary/5">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 text-primary animate-spin" />
              <span className="font-medium text-foreground">Processing CSV import...</span>
            </div>
          </div>
        )}

        {csvUploadMutation.isSuccess && (
          <div className="mt-4 p-4 rounded-lg border border-success bg-success-subtle">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-success" />
              <span className="font-medium text-success-foreground">CSV import successful!</span>
            </div>
            <p className="text-sm text-success-foreground mt-2">
              Created: {csvUploadMutation.data.created ?? 0}, Updated:{' '}
              {csvUploadMutation.data.updated ?? 0}, Total: {csvUploadMutation.data.total ?? 0}
            </p>
          </div>
        )}

        {csvUploadMutation.isError && (
          <div className="mt-4 p-4 rounded-lg border border-danger bg-danger-subtle">
            <div className="flex items-center gap-3">
              <XCircle className="h-5 w-5 text-danger" />
              <span className="font-medium text-danger-foreground">CSV import failed</span>
            </div>
            {(csvUploadMutation.error as any)?.errors && (
              <ImportErrorList errors={(csvUploadMutation.error as any).errors} />
            )}
          </div>
        )}

        {/* Workbook Upload Status */}
        {workbookMutation.isPending && (
          <div className="mt-4 p-4 rounded-lg border border-primary bg-primary/5">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 text-primary animate-spin" />
              <span className="font-medium text-foreground">Importing all sheets...</span>
            </div>
          </div>
        )}

        {workbookMutation.isSuccess && !workbookPreview && (
          <WorkbookSuccessMessage result={workbookMutation.data} />
        )}

        {workbookMutation.isError && (
          <div className="mt-4 p-4 rounded-lg border border-danger bg-danger-subtle">
            <div className="flex items-center gap-3">
              <XCircle className="h-5 w-5 text-danger" />
              <span className="font-medium text-danger-foreground">Workbook import failed</span>
            </div>
            <p className="text-sm text-danger-foreground mt-2">
              {(workbookMutation.error as any)?.message ??
                (workbookMutation.error as any)?.error ??
                'An error occurred during import'}
            </p>
          </div>
        )}
      </div>

      {/* Import History */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">Import History</h2>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-danger bg-danger-subtle p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-danger" />
              <div>
                <h3 className="font-medium text-danger-foreground">
                  Failed to load import history
                </h3>
                <p className="text-sm text-danger-foreground mt-1">
                  {error instanceof Error ? error.message : 'Unknown error'}
                </p>
              </div>
            </div>
          </div>
        ) : imports && imports.length > 0 ? (
          <div className="bg-background rounded-lg border border-border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background-subtle">
                  <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                    Rows
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                    File
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {imports.map((imp) => (
                  <tr
                    key={imp.id}
                    className="hover:bg-background-subtle transition-colors"
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4 text-foreground-muted" />
                        <span className="text-sm font-medium text-foreground">
                          {imp.importType.replace(/_/g, ' ').replace(/-/g, ' ')}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <StatusBadge status={imp.status as ImportStatus} />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground-muted">
                      {(imp.rowCount ?? 0).toLocaleString()} rows
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground-muted font-mono">
                      {imp.fileName || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground-muted">
                      {formatDistanceToNow(new Date(imp.createdAt), { addSuffix: true })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-border bg-background p-8 text-center">
            <FileSpreadsheet className="h-8 w-8 text-foreground-muted mx-auto mb-3" />
            <p className="text-foreground-muted">No imports yet</p>
            <p className="text-sm text-foreground-muted mt-1">
              Drop a GP Excel workbook above to get started
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function isImportError(entry: WorkbookImportEntry): entry is { error: string } {
  return 'error' in entry;
}

function WorkbookSuccessMessage({ result }: { result: WorkbookImportResult }) {
  const hasErrors = (result.salesOrders && isImportError(result.salesOrders)) ||
    (result.purchaseOrders && isImportError(result.purchaseOrders)) ||
    (result.inventory && isImportError(result.inventory));

  return (
    <div className={`mt-4 p-4 rounded-lg border ${hasErrors ? 'border-warning bg-warning-subtle' : 'border-success bg-success-subtle'}`}>
      <div className="flex items-center gap-3 mb-2">
        {hasErrors ? (
          <>
            <AlertTriangle className="h-5 w-5 text-warning" />
            <span className="font-medium text-foreground">Workbook import completed with errors</span>
          </>
        ) : (
          <>
            <CheckCircle className="h-5 w-5 text-success" />
            <span className="font-medium text-success-foreground">Workbook import successful!</span>
          </>
        )}
      </div>
      <div className="text-sm space-y-1">
        {result.salesOrders && (
          isImportError(result.salesOrders) ? (
            <p className="text-danger-foreground">Sales Orders: {result.salesOrders.error}</p>
          ) : (
            <p className="text-success-foreground">
              Sales Orders: {result.salesOrders.created ?? 0} created, {result.salesOrders.updated ?? 0} updated
            </p>
          )
        )}
        {result.purchaseOrders && (
          isImportError(result.purchaseOrders) ? (
            <p className="text-danger-foreground">Purchase Orders: {result.purchaseOrders.error}</p>
          ) : (
            <p className="text-success-foreground">
              Purchase Orders: {result.purchaseOrders.total ?? 0} POs ({(result.purchaseOrders as any).lineItems ?? 0} line items)
            </p>
          )
        )}
        {result.inventory && (
          isImportError(result.inventory) ? (
            <p className="text-danger-foreground">Inventory: {result.inventory.error}</p>
          ) : (
            <p className="text-success-foreground">
              Inventory: {result.inventory.created ?? 0} created, {result.inventory.updated ?? 0} updated ({result.inventory.total ?? 0} rows)
            </p>
          )
        )}
      </div>
    </div>
  );
}
